import { useState } from 'react';

import { useMutation, useQuery } from '@edreamsodigeo/frontend-api-client';
import { useLogFrontendApiError } from '@edreamsodigeo/frontend-core';

import {
  offerQuery,
  multiOfferQuery,
  countryQuery,
  elegibilityQuery,
  getMembershipAccountProperties,
  GET_USER,
} from './queries';
import { addContactDetails, create, get } from './shoppingBasket';
import {
  getMembership,
  updateMembershipProduct,
  membershipProductEvaluation,
  getUserMembership,
  updateUserMembershipMutation,
} from './membership';
import {
  createShoppingBundle,
  getRepricingFromShoppingBundle,
  getShoppingBundleWithBookingId,
} from './shoppingBundle';
import { createPriceFreeze, getItineraryPriceFreeze } from './priceFreeze';
import { getSubscriptionOffers } from '@/hooks/GQL/subscription';

const SB_VERSION = 'BASKET_V3';
const ADD_MUTATION_ACTION = 'ADD';

export function usePriceFreezeOfferQuery({
  searchId = '',
  fareItineraryKey = '',
  segmentKeys = [],
  lazyCondition = false,
}) {
  const data = useQuery(offerQuery, {
    variables: {
      offerRequest: {
        searchId: String(searchId),
        fareItineraryKey: String(fareItineraryKey),
        segmentKeys: segmentKeys.map((key) => Number(`${key}`.split(',')[0])),
      },
    },
    skip:
      !isValidInput(searchId, fareItineraryKey, segmentKeys) || lazyCondition,
  });

  useLogFrontendApiError(data);

  return data;
}

export function usePriceFreezeMultiOfferQuery({
  searchId = '',
  fareItineraryKey = '',
  segmentKeys = [],
}) {
  const data = useQuery(multiOfferQuery, {
    variables: {
      offerRequest: {
        searchId: String(searchId),
        fareItineraryKey: String(fareItineraryKey),
        segmentKeys: segmentKeys.map((key) => Number(`${key}`.split(',')[0])),
      },
    },
    skip: !isValidInput(searchId, fareItineraryKey, segmentKeys),
  });
  useLogFrontendApiError(data);
  return data;
}

function isValidInput(searchId, fareItineraryKey, segmentKeys) {
  return (
    Number(searchId) > 0 &&
    Number(fareItineraryKey) >= 0 &&
    segmentKeys.map(Number).filter((key) => key >= 0).length ===
      segmentKeys.length
  );
}

export function useEligibilityQuery(searchCriteria = {}) {
  return useQuery(elegibilityQuery, {
    variables: {
      priceFreezeEligibilityRequest: searchCriteria,
    },
  });
}

export function useCountries({ locale = '' } = {}) {
  return useQuery(countryQuery, {
    variables: {
      getCountriesRequest: {
        locale,
      },
    },
  });
}

export function useCreateShoppingBasketMutation({ onSuccess, onError }) {
  const { loading, errors, mutate } = useMutation(create, {
    onSuccess,
    onError,
  });

  return {
    loading,
    errors,
    mutate: () => {
      return mutate({
        request: { shoppingType: 'BASKET_V3', bundleId: null },
      });
    },
  };
}

export function useShoppingBasket({ shoppingBasketId = '' }) {
  return useQuery(get, {
    variables: { request: { shoppingType: SB_VERSION, shoppingBasketId } },
  });
}

export function useGetShoppingBasketWithBookingId({
  shoppingId,
  productId,
  onSuccess,
}) {
  return useQuery(getShoppingBundleWithBookingId, {
    onSuccess,
    variables: {
      shoppingBundleRequest: {
        shoppingInfo: { shoppingType: SB_VERSION, shoppingId },
      },
      getShoppingBookingIdRequest: {
        shoppingInfo: {
          shoppingType: SB_VERSION,
          shoppingId: shoppingId,
        },
      },
      itineraryPriceFreezeRevenueDataRequest: {
        itineraryPriceFreezeId: productId,
      },
    },
  });
}

export function useCreatePriceFreeze({ onSuccess, onError }) {
  return useMutation(createPriceFreeze, {
    onSuccess,
    onError,
  });
}

export function useGetShoppingCart({ bookingId }) {
  return useQuery(getRepricingFromShoppingBundle, {
    skip: Boolean(bookingId) === false,
    variables: {
      request: {
        shoppingInfo: {
          shoppingId: bookingId,
          shoppingType: 'CART',
        },
      },
    },
  });
}

export function useCreateShoppingCart({ request }) {
  const [bookingId, setBookingId] = useState(null);
  const [isRequested, setRequested] = useState(false);
  const { mutate, loading } = useMutation(createShoppingBundle, {
    onSuccess(result) {
      setRequested(true);
      setBookingId(
        result?.data?.createShoppingBundle?.shoppingBundle?.shoppingId
      );
    },
    onError() {
      setRequested(true);
      setBookingId(0);
    },
  });

  if (!isRequested && !loading) {
    mutate(request);
  }

  return {
    loading: Boolean(bookingId === null),
    bookingId,
  };
}

export const useMembershipAccountPropertiesQuery = ({
  userEmail = '',
  isLogged,
} = {}) => {
  return useQuery(getMembershipAccountProperties, {
    skip: Boolean(isLogged) === true || Boolean(userEmail) === false,
    variables: {
      email: userEmail,
    },
  });
};

export const useAddSubscriptionMutation = ({ onSuccess, onError }) => {
  const { loading, errors, mutate } = useMutation(updateMembershipProduct, {
    onSuccess,
    onError,
  });

  return {
    loading,
    errors,
    mutate: (args) => {
      return mutate({
        updateMembershipProductRequest: {
          action: ADD_MUTATION_ACTION,
          shoppingInfo: {
            shoppingId: args.shoppingId,
            shoppingType: SB_VERSION,
          },
          offerId: args.offerId,
          ...args.buyer,
        },
      });
    },
  };
};

export const useSubscriptionOffersQuery = () => {
  return useQuery(getSubscriptionOffers, {
    variables: {
      request: {
        touchpoint: 'PRICE_FREEZE',
      },
    },
  });
};

export const useMembershipQuery = ({ isPrime, shoppingBasketId }) => {
  return useQuery(getMembership, {
    skip: isPrime || !shoppingBasketId,
    variables: {
      membershipProductRequest: {
        shoppingInfo: {
          shoppingId: shoppingBasketId,
          shoppingType: SB_VERSION,
        },
      },
    },
  });
};

export const useMembershipProductEvaluationMutation = ({
  onSuccess,
  onError,
}) => {
  const { loading, errors, mutate } = useMutation(membershipProductEvaluation, {
    onSuccess,
    onError,
  });

  return {
    loading,
    errors,
    mutate: ({ creditCard = {}, shoppingId = '' }) => {
      return mutate({
        request: {
          paymentInstrumentToken: creditCard.token,
          creditCardBin: creditCard.bin,
          creditCardDate: `20${creditCard.expirationYear}-${creditCard.expirationMonth}`,
          shoppingInfo: {
            shoppingId: shoppingId,
            shoppingType: SB_VERSION,
          },
        },
      });
    },
  };
};

export const useGetItineraryPriceFreeze = ({ id }) => {
  return useQuery(getItineraryPriceFreeze, {
    variables: {
      id,
    },
  });
};

export const useGetUserQuery = ({ onSuccess, onError } = {}) => {
  return useQuery(GET_USER, {
    onSuccess,
    onError,
  });
};

export const useContactDetailsToShoppingBasketMutation = ({
  onSuccess,
  onError,
}) =>
  useMutation(addContactDetails, {
    onSuccess,
    onError,
  });

export const useUserMembershipQuery = ({ onSuccess, onError } = {}) => {
  return useQuery(getUserMembership, {
    onSuccess,
    onError,
  });
};

export const useUpdateUserMembershipMutation = ({
  onSuccess,
  onError,
} = {}) => {
  const { loading, errors, mutate } = useMutation(
    updateUserMembershipMutation,
    {
      onSuccess,
      onError,
    }
  );

  return {
    loading,
    errors,
    mutate: ({ action }) => {
      return mutate({
        action,
      });
    },
  };
};
